import { Component, lazy, Suspense, type KeyboardEvent, type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Smile } from "lucide-react";
import { Z_ABOVE_DOCK } from "../lib/zLayers";

// Figma "Emoji button" 3215:7 and "Web / Emoji / Picker popover" 3217:88 (design page 3215:2).
// The picker (Frimousse and its data loader) is its own chunk, fetched the first time it opens.
const EmojiPopover = lazy(() => import("./EmojiPopover"));

const POPOVER_WIDTH = 344;
const POPOVER_HEIGHT = 372;
const MARGIN = 8;

interface Placement {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Above the button when it fits, else below; always inside the window, so it also fits a phone-width page. */
function place(button: HTMLElement): Placement {
  const r = button.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.min(POPOVER_WIDTH, vw - 2 * MARGIN);
  const above = r.top - MARGIN - 4;
  const below = vh - r.bottom - MARGIN - 4;
  const up = above >= Math.min(POPOVER_HEIGHT, below);
  const height = Math.max(Math.min(POPOVER_HEIGHT, up ? above : below), 160);
  const top = up ? Math.max(MARGIN, r.top - 4 - height) : Math.min(r.bottom + 4, vh - MARGIN - height);
  const left = Math.min(Math.max(r.left, MARGIN), vw - MARGIN - width);
  return { top, left, width, height };
}

const samePlacement = (a: Placement, b: Placement) =>
  a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;

/** The popover hangs off the body: above the dock, and above any layer the button sits in (post dialog, composer). */
function layerAbove(button: HTMLElement): number {
  let top = Z_ABOVE_DOCK - 1;
  for (let n = button.parentElement; n; n = n.parentElement) {
    const z = Number.parseInt(getComputedStyle(n).zIndex, 10);
    if (z > top) top = z;
  }
  return top + 1;
}

/** Frimousse throws when its data fails to load, and so does a failed chunk: show a line, not a blank box. */
class PickerBoundary extends Component<{ message: string; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? (
      <p role="alert" data-testid="emoji-load-failed" className="m-0 p-3 text-xs text-[var(--foreground-muted)]">{this.props.message}</p>
    ) : (
      this.props.children
    );
  }
}

interface Props {
  /** One pick; the popover stays open for more. */
  onPick: (emoji: string) => void;
  /** The popover closed by Escape, an outside click or the button: put focus back in the field. */
  onClose?: () => void;
  /** `emoji-button`, suffixed by place where a screen has several (`emoji-button-comment`). */
  testId?: string;
}

/** The smiley icon button that opens the emoji picker popover. */
export default function EmojiButton({ onPick, onClose, testId = "emoji-button" }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState<Placement | null>(null);
  const [layer, setLayer] = useState(Z_ABOVE_DOCK);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const close = useCallback((outside = false) => {
    setOpen(false);
    // After the click's own focus change. An outside click that landed on another field keeps its focus:
    // only refocus when nothing took it.
    setTimeout(() => {
      const a = document.activeElement;
      if (outside && a && a !== document.body) return;
      onCloseRef.current?.();
    }, 0);
  }, []);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const el = buttonRef.current;
    const update = (e?: Event) => {
      // The capture listener also hears the picker's own list scrolling, which moves nothing.
      if (e?.target instanceof Node && popoverRef.current?.contains(e.target)) return;
      const next = place(el);
      setAt((prev) => (prev && samePlacement(prev, next) ? prev : next));
    };
    setLayer(layerAbove(el));
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // Capture on window: Escape closes only the picker, not the modal (composer, Blog) it sits in.
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopImmediatePropagation();
      close();
    };
    const onDown = (e: PointerEvent) => {
      const n = e.target as Node;
      if (popoverRef.current?.contains(n) || buttonRef.current?.contains(n)) return;
      close(true);
    };
    window.addEventListener("keydown", onKey, true);
    document.addEventListener("pointerdown", onDown, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      document.removeEventListener("pointerdown", onDown, true);
    };
  }, [open, close]);

  // In the body, the Tab order no longer runs from the button into the popover by itself.
  const onButtonKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (!open || e.key !== "Tab" || e.shiftKey) return;
    const first = popoverRef.current?.querySelector<HTMLElement>("input, button");
    if (!first) return;
    e.preventDefault();
    first.focus();
  };
  const onPopoverKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab" || !e.shiftKey) return;
    if (e.target !== popoverRef.current?.querySelector("input, button")) return;
    e.preventDefault();
    buttonRef.current?.focus();
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        data-testid={testId}
        aria-label={t("emoji_button_a11y", "Emoji")}
        aria-haspopup="dialog"
        aria-expanded={open}
        // The field keeps its caret and selection: the button does not take focus on press.
        onMouseDown={(e) => e.preventDefault()}
        onKeyDown={onButtonKey}
        onClick={() => (open ? close() : setOpen(true))}
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full p-0 ${
          open
            ? "bg-[var(--primary-muted)] text-[var(--primary-on-surface)]"
            : "bg-transparent text-[var(--foreground-muted)] hover:bg-[var(--bg-hover)]"
        }`}
      >
        <Smile size={24} aria-hidden />
      </button>
      {open && at && createPortal(
        <div
          ref={popoverRef}
          role="dialog"
          aria-label={t("emoji_button_a11y", "Emoji")}
          data-testid={`${testId}-popover`}
          onKeyDown={onPopoverKey}
          style={{ position: "fixed", top: at.top, left: at.left, width: at.width, height: at.height, zIndex: layer }}
          className="flex flex-col overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--bg-surface)] p-3 shadow-[0_1px_2px_rgba(15,23,42,0.12)]"
        >
          <PickerBoundary message={t("emoji_load_failed", "Emoji could not be loaded. Close and try again.")}>
            <Suspense fallback={null}>
              <EmojiPopover onPick={onPick} width={at.width} />
            </Suspense>
          </PickerBoundary>
        </div>,
        // The button's own document: a Picture-in-Picture window has its own body.
        buttonRef.current?.ownerDocument.body ?? document.body,
      )}
    </>
  );
}
